import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { useSparks } from '../../contexts/SparksContext';
import { SECTION_TIPS, SECTION_TIP_DELAY_MS, TIPS, TIP_AUTO_DISMISS_MS, noteNavClick, takePendingTip, takeSectionTip } from './sparksTips';
import { markShopAnnounced } from './shopNudge';
import './sparks.css';

const BUBBLE_WIDTH = 300;
const GUTTER = 12;

/**
 * Speech bubble under a nav element (`data-sparks-tip="shop" | "sparks" | <section>`) nudging the child to the
 * shop, to earning Sparks, or explaining what a section pays. A login or nav trigger shows the shop or Sparks tip,
 * then, once that closes for any reason, one section tip in rotation. On a phone the nav tabs sit in the menu, so
 * a tab's bubble points at the Menu button (`data-sparks-menu`) instead. Rendered inside the logged-in nav, which
 * is unmounted on question pages, so it never covers a quiz. Also counts nav link clicks (`[data-sparks-nav] a[href]`)
 * for the every-N-clicks trigger; those links are plain anchors that reload the page, so the count
 * is written to storage synchronously before the browser navigates.
 */
export default function SparksCoachMark({ childId, announceCanBuy = false, balance = 0 }) {
  const location = useLocation();
  const { earnRates } = useSparks();
  const [shown, setShown] = useState(null); // { name, target, text, trigger, followUp }
  const [pos, setPos] = useState(null); // { top, left, arrowX, menu }
  const shownRef = useRef(null);
  shownRef.current = shown;
  const ratesRef = useRef(earnRates);
  ratesRef.current = earnRates;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const followTimer = useRef(null);

  const show = useCallback((tip) => {
    setShown(tip);
    posthog.capture('sparks_tip_shown', { tip: tip.name, trigger: tip.trigger });
  }, []);

  const close = useCallback(() => {
    const was = shownRef.current;
    setShown(null);
    if (!was?.followUp) return;
    clearTimeout(followTimer.current);
    followTimer.current = setTimeout(() => {
      const rates = ratesRef.current;
      if (!rates) return;
      const section = takeSectionTip(childId, { pathname: pathRef.current });
      if (!section) return;
      const { target, text } = SECTION_TIPS[section];
      show({ name: section, target, text: text(rates), trigger: was.trigger, followUp: false });
    }, SECTION_TIP_DELAY_MS);
  }, [childId, show]);

  useEffect(() => () => clearTimeout(followTimer.current), []);

  useEffect(() => {
    if (!childId) return undefined;
    const onClick = (e) => {
      if (e.target?.closest?.('[data-sparks-nav] a[href]')) noteNavClick(childId);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [childId]);

  useEffect(() => {
    const next = takePendingTip(childId, { avoid: location.pathname === '/shop' ? 'shop' : null });
    if (!next) return;
    show({ name: next.tip, ...TIPS[next.tip], trigger: next.trigger, followUp: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  // The shop alert has news: a price the child's Sparks newly cover. Said once, ahead of any rotation tip.
  useEffect(() => {
    if (!announceCanBuy || !childId) return;
    markShopAnnounced(childId, balance);
    show({ name: 'canBuy', ...TIPS.canBuy, trigger: 'affordable', followUp: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announceCanBuy, childId]);

  const onMenu = !!pos?.menu;
  useEffect(() => {
    if (!shown) return undefined;
    const t = setTimeout(close, TIP_AUTO_DISMISS_MS);
    const onTargetClick = (e) => {
      const el = e.target?.closest?.(onMenu ? '[data-sparks-menu]' : `[data-sparks-tip="${shown.target}"]`);
      if (el) {
        posthog.capture('sparks_tip_clicked', { tip: shown.name, trigger: shown.trigger });
        close();
      }
    };
    document.addEventListener('click', onTargetClick, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onTargetClick, true);
    };
  }, [shown, onMenu, close]);

  useLayoutEffect(() => {
    if (!shown) return undefined;
    const place = () => {
      const firstVisible = (selector) =>
        [...document.querySelectorAll(selector)].map((el) => el.getBoundingClientRect()).find((rect) => rect.width);
      // The first one on screen: the desktop tab, or on a phone the Menu button standing in for it.
      let r = firstVisible(`[data-sparks-tip="${shown.target}"]`);
      const menu = !r;
      if (menu) r = firstVisible('[data-sparks-menu]');
      if (!r) {
        setPos(null);
        return;
      }
      const width = Math.min(BUBBLE_WIDTH, window.innerWidth - GUTTER * 2);
      const centre = r.left + r.width / 2;
      const left = Math.min(Math.max(centre - width / 2, GUTTER), window.innerWidth - GUTTER - width);
      setPos({ top: r.bottom + window.scrollY + 10, left: left + window.scrollX, width, arrowX: centre - left, menu });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [shown]);

  if (!shown || !pos) return null;

  return createPortal(
    <div className="sparks-coach" role="status" style={{ top: pos.top, left: pos.left, width: pos.width, '--arrow-x': `${pos.arrowX}px` }} data-testid="sparks-coach-mark">
      <span>{shown.text}</span>
      <button type="button" className="sparks-coach__ok" onClick={close}>Got it</button>
    </div>,
    document.body,
  );
}
